/**
 * ticketTotalesModel.ts — Modelo de filas de "Totales de venta" del ticket.
 *
 * Regla de presentación (web y térmica IDÉNTICAS):
 *  - El impuesto (IVA/Impoconsumo) va incluido en el PVP: el ticket muestra
 *    primero los descuentos —cada concepto en su PROPIA fila y orden:
 *    precio lista → MAYORISTA → VIP → ítems → GLOBAL— y, cuando hay impuesto,
 *    muestra "Subtotal" (neto final CON IVA incluido) + la(s) fila(s) de IVA
 *    por tarifa rotuladas "(incluido)", que son informativas y NO se suman.
 *  - Sin impuesto: se conserva el desglose de descuentos plano.
 *
 * Mayorista y VIP son SIEMPRE conceptos separados (nunca "mayorista/VIP").
 *
 * El IVA se calcula sobre el NETO FINAL de cada línea (tras descuentos de ítem
 * y su parte del global), de modo que base + IVA cuadra con el total cobrado.
 */
import type { CartItem } from "@/types";
import type { DiscountBreakdown, LineDiscount } from "./discountHelper";
import {
  buildTotalesConceptos,
  getLineCobradoTotal,
} from "./discountAttribution";
import { resolveLineTaxRatePct } from "./ticketTaxBreakdown";
import { taxKindLabel } from "@/lib/constants/taxRates";
import { getCartLineId } from "./cartDiscountUtils";

export interface TicketTotalesRow {
  key: string;
  label: string;
  amount: number;
  /** warn = descuento (color ámbar, signo −); info = impuesto; muted = base/neto. */
  tone: "muted" | "warn" | "info";
}

export interface TicketTotalesModel {
  rows: TicketTotalesRow[];
  hasLineDiscounts: boolean;
}

/** Desglose neto final por tarifa (tras descuentos de ítem y global). */
function computeFinalTaxSplit(
  cart: CartItem[],
  breakdown?: DiscountBreakdown,
  defaultRatePct = 0,
): {
  baseNeto: number;
  byRate: { ratePct: number; iva: number }[];
  hasIva: boolean;
} {
  const byRateMap = new Map<number, number>();
  let baseNeto = 0;
  let totalIva = 0;

  cart.forEach((item, index) => {
    const lineId = getCartLineId(index, item);
    const fromBreakdown = breakdown?.lines.find((l) => l.lineId === lineId);
    // netoItem: tras descuento de ítem + parte proporcional del global (si existe).
    const neto = Math.max(
      0,
      Math.round(
        fromBreakdown?.netoItem ??
          fromBreakdown?.netoAntesGlobal ??
          getLineCobradoTotal(item),
      ),
    );
    const rate = resolveLineTaxRatePct(item, defaultRatePct);
    if (rate > 0) {
      const base = Math.round(neto / (1 + rate / 100));
      const iva = neto - base;
      baseNeto += base;
      totalIva += iva;
      byRateMap.set(rate, (byRateMap.get(rate) ?? 0) + iva);
    } else {
      baseNeto += neto;
    }
  });

  const byRate = [...byRateMap.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([ratePct, iva]) => ({ ratePct, iva }));

  return { baseNeto, byRate, hasIva: totalIva > 0 };
}

/**
 * Construye las filas de Totales del ticket. Cuando hay impuesto incluido
 * muestra: [descuentos] → "Subtotal" (con IVA) → filas de IVA "(incluido)"
 * informativas → (servicio/recargos/total se agregan en la superficie).
 * Sin impuesto: desglose plano.
 */
export function buildTicketTotalesModel(
  cart: CartItem[],
  breakdown?: DiscountBreakdown,
  globalDiscount?: LineDiscount | null,
  defaultRatePct = 0,
): TicketTotalesModel {
  const concepts = buildTotalesConceptos(
    cart,
    breakdown ?? { descuentoGlobal: 0 },
    globalDiscount ?? null,
  );
  const hasLineDiscounts =
    concepts.descuentoMayorista > 0 ||
    concepts.descuentoVIP > 0 ||
    concepts.descuentoApilado > 0;

  const tax = computeFinalTaxSplit(cart, breakdown, defaultRatePct);
  const rows: TicketTotalesRow[] = [];

  if (tax.hasIva) {
    // Descuentos sobre el precio de lista (informativos, incluyen IVA).
    if (hasLineDiscounts) {
      rows.push({
        key: "lista",
        label: concepts.baseLabel,
        amount: concepts.subtotalLista,
        tone: "muted",
      });
    }
    if (concepts.descuentoMayorista > 0) {
      rows.push({
        key: "mayorista",
        label: "Descuento mayorista",
        amount: concepts.descuentoMayorista,
        tone: "warn",
      });
    }
    if (concepts.descuentoVIP > 0) {
      rows.push({
        key: "vip",
        label: "Descuento cliente VIP",
        amount: concepts.descuentoVIP,
        tone: "warn",
      });
    }
    if (concepts.descuentoApilado > 0) {
      rows.push({
        key: "items",
        label: "Descuentos por ítem",
        amount: concepts.descuentoApilado,
        tone: "warn",
      });
    }
    if (breakdown && breakdown.descuentoGlobal > 0) {
      rows.push({
        key: "global",
        label:
          concepts.rows.find((r) => r.id === "global")?.label ??
          "Descuento General",
        amount: breakdown.descuentoGlobal,
        tone: "warn",
      });
    }
    // Neto final CON IVA incluido (después de todos los descuentos): así la
    // lectura cuadra por resta (Subtotal precio lista − descuentos = Subtotal).
    // El IVA se informa por separado como "incluido", sin volver a sumarlo.
    const subtotalConIva =
      tax.baseNeto + tax.byRate.reduce((s, b) => s + b.iva, 0);
    rows.push({
      key: "subtotal",
      label: "Subtotal",
      amount: subtotalConIva,
      tone: "muted",
    });
    for (const bucket of tax.byRate) {
      rows.push({
        key: `iva-${bucket.ratePct}`,
        label: `${taxKindLabel(bucket.ratePct)} ${bucket.ratePct}% (incluido)`,
        amount: bucket.iva,
        tone: "info",
      });
    }
    return { rows, hasLineDiscounts };
  }

  // Sin impuesto: desglose plano (Subtotal + descuentos, si los hay). La base
  // siempre se incluye para anclar la lectura (el térmico la omite si es única
  // e igual al total).
  for (const row of concepts.rows) {
    rows.push({
      key: row.id,
      label: row.label,
      amount: row.amount,
      tone: row.id === "base" ? "muted" : "warn",
    });
  }
  return { rows, hasLineDiscounts };
}
